import React, { useState, useRef, useCallback, useMemo } from 'react';
import { Box, Typography, Button, Container, Paper, Divider, Dialog, Slide } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { ISaleObject } from '../../types/global/helper';
import { PaymentIntent, StripeElementsOptions } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import {
    IPaymentMethodSelectorRef,
    IPType,
    isPaymentIntentResult,
    PaymentMethodSelector,
} from '../Checkout/PaymentMethodSelector';
import { getCurrencySymbol, getJWtDetails, useDispatchPromise } from '../../utils/AppUtils';
import { showToastMessage } from '../../utils/AppUtils';
import { ICheckoutItem, ICurrencyInfo } from '../../redux/checkout/types';
import CustomLazyLoadImage from '../Image/CustomLazyLoadImage';
import CloseIcon from '../CustomSVGIcons/CloseIcon';
import { TransitionProps } from '@mui/material/transitions';
import { IPaymentCard } from '../../types/global/generic.types';
import { processPayment } from '../../redux/checkout/action';
import { useStripePromise } from '../../hook/useStripePromise';
import { createPaymentIntentForPurchase } from '../../redux/payment/action';
import type { IPaystackPurchaseResponse } from '../../redux/payment/types';
import { usePaymentRail } from '../../hook/usePaymentRail';
import {
    chargeCurrencyFromRail,
    flutterwaveCheckoutAvailable,
    paystackCheckoutAvailable,
    resolvePaymentRail,
    stripeCheckoutAvailable,
    stripePublishableKeyFromRail,
} from '../../utils/paymentRail';

export interface IGenericPaymentItem {
    id: string;
    title: string;
    description?: string;
    /** Keep this value  in cents as it is converted back into cents inside this modal */
    price: number;
    original_price?: number;
    currency?: string;
    quantity?: number;
    image?: string;
    breakdown?: Record<string, any>;
    details?: Array<{
        label: string;
        value: number;
    }>;
}

export interface IGenericPaymentModalProps {
    open: boolean;
    onClose: () => void;
    title?: string;
    items: IGenericPaymentItem[];
    onPaymentComplete: ({ paymentIntent, sale }: { paymentIntent: PaymentIntent; sale: any }) => void;
    saleObject?: ISaleObject;
    currencyInfo?: ICurrencyInfo;
    showItemImages?: boolean;
    showItemDetails?: boolean;
    customTotalCalculation?: (items: IGenericPaymentItem[]) => number;
    additionalFees?: Array<{
        label: string;
        amount: number;
        description?: string;
    }>;
    maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    category?: string;
    amountsInCents?: boolean; // If true, all amounts are in cents and will be converted for display
    additionalMetadata?: Record<any, any>;
    /** Called after Paystack redirect verify+complete. Caller should handle submission with the returned saleId. */
    onPaystackComplete?: (reference: string, saleId?: string) => void;
    /** Key under which context is stored in sessionStorage for Paystack return handling */
    paystackSessionKey?: string;
}

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const GenericPaymentModal: React.FC<IGenericPaymentModalProps> = ({
    open,
    onClose,
    title = 'Complete Purchase',
    items,
    onPaymentComplete,
    currencyInfo,
    saleObject,
    showItemImages = true,
    showItemDetails = true,
    customTotalCalculation,
    additionalFees = [],
    maxWidth = 'md',
    category = 'purchase',
    amountsInCents = false,
    additionalMetadata,
    onPaystackComplete,
    paystackSessionKey = 'generic_paystack_session',
}) => {
    const { themeStyle, device } = useThemeContext();
    const [isValidCard, setIsValidCard] = useState(false);
    const [paymentType, setPaymentType] = useState<IPType>();
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<IPaymentCard | null>(null);
    const [saveForFuture, setSaveForFuture] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [paystackLoading, setPaystackLoading] = useState(false);
    const paymentRef = useRef<IPaymentMethodSelectorRef | null>(null);
    const dispatchPromise = useDispatchPromise();
    const { user } = getJWtDetails();
    const symbol = getCurrencySymbol(currencyInfo?.transaction_currency ?? 'usd');
    const { rail: railData } = usePaymentRail(category, open);
    const paymentRail = resolvePaymentRail(railData);
    const canUsePaystack = paystackCheckoutAvailable(paymentRail);
    const canUseFlutterwave = flutterwaveCheckoutAvailable(paymentRail);
    const canUseStripe = stripeCheckoutAvailable(paymentRail);
    const stripePromise = useStripePromise(canUseStripe ? stripePublishableKeyFromRail(paymentRail) : null, {
        enabled: open && canUseStripe,
    });
    const checkoutCurrency = chargeCurrencyFromRail(paymentRail, currencyInfo?.transaction_currency || 'usd');
    const railProcessorMeta = paymentRail?.processor_fee_profile
        ? { processor_fee_profile: paymentRail.processor_fee_profile }
        : {};

    // Helper functions for cents conversion
    const convertCentsToDollars = useCallback(
        (cents: number) => {
            return amountsInCents ? cents / 100 : cents;
        },
        [amountsInCents],
    );

    const convertDollarsToCents = useCallback(
        (dollars: number) => {
            return amountsInCents ? Math.round(dollars * 100) : dollars;
        },
        [amountsInCents],
    );

    // Calculate total amount
    const calculateTotal = useCallback(() => {
        if (customTotalCalculation) {
            // Custom calculation should work with the raw item prices and return the final total in dollars
            // The function receives items with their raw prices (could be cents or dollars)
            // and should return the final total in dollars for display
            return customTotalCalculation(items);
        }

        const itemsTotal = items.reduce((sum, item) => {
            const quantity = item.quantity || 1;
            const itemPrice = convertCentsToDollars(item.price);
            return sum + itemPrice * quantity;
        }, 0);

        const feesTotal = additionalFees.reduce((sum, fee) => {
            const feeAmount = convertCentsToDollars(fee.amount);
            return sum + feeAmount;
        }, 0);

        return itemsTotal + feesTotal;
    }, [items, additionalFees, customTotalCalculation, convertCentsToDollars]);

    const totalAmount = calculateTotal();

    // Convert items to checkout format
    const checkoutItems: ICheckoutItem[] = useMemo(() => {
        return items.map(item => ({
            id: item.id,
            tierId: item.id,
            title: item.title,
            description: item.description,
            price: convertCentsToDollars(item.price),
            original_price: convertCentsToDollars(item.original_price || item.price),
            currency: item.currency || 'usd',
            quantity: item.quantity || 1,
            image: item.image,
            breakdown: item.breakdown,
            details: item.details,
        }));
    }, [items, convertCentsToDollars]);

    const handleCompletePurchase = async (paymentIntent: PaymentIntent) => {
        try {
            const payload: ICheckoutItem = {
                id: `generic-purchase-${Date.now()}`,
                tierId: `generic-purchase-${Date.now()}`,
                title: title,
                price: totalAmount,
                original_price: totalAmount,
                currency: currencyInfo?.transaction_currency || 'usd',
                quantity: 1,
                breakdown: {
                    items: checkoutItems,
                    additionalFees,
                    total: totalAmount,
                },
            };

            dispatchPromise(
                processPayment({
                    items: [payload],
                    total: totalAmount,
                    paymentMethod: selectedPaymentMethod as IPaymentCard,
                    currency: currencyInfo?.transaction_currency || 'usd',
                    category,
                    metadata: {
                        email: user?.email,
                        name: `${user?.firstName} ${user?.lastName}`,
                        currencyInfo: currencyInfo,
                        paymentIntent,
                        items: checkoutItems,
                        additionalFees,
                        ...railProcessorMeta,
                        ...additionalMetadata,
                    },
                    saleObject:
                        saleObject ||
                        ({
                            category,
                            item: checkoutItems,
                            transaction_currency: currencyInfo?.transaction_currency || 'usd',
                        } as ISaleObject),
                }),
            ).then((sale: any) => {
                if (sale) {
                    onPaymentComplete({ paymentIntent, sale });
                }
            });
        } catch (error) {
            console.error('Payment processing error:', error);
            showToastMessage('Payment processing failed. Please try again.', 'error');
        }
    };

    const handleSubmit = async () => {
        setProcessing(true);
        try {
            const payload: ICheckoutItem = {
                id: `generic-purchase-${Date.now()}`,
                tierId: `generic-purchase-${Date.now()}`,
                title: title,
                price: totalAmount,
                original_price: totalAmount,
                currency: currencyInfo?.transaction_currency || 'usd',
                quantity: 1,
                breakdown: {
                    items: checkoutItems,
                    additionalFees,
                    total: totalAmount,
                },
            };

            await paymentRef.current?.handleSubmit(
                currencyInfo?.transaction_currency || 'usd',
                Math.max(convertDollarsToCents(totalAmount), 1),
                {
                    email: user?.email || '',
                    name: user ? `${user.firstName} ${user.lastName}` : '',
                    saveCard: saveForFuture,
                    address: {
                        line1: '',
                        line2: '',
                        city: '',
                        state: '',
                        postal_code: '',
                        country: 'US',
                    },
                },
                {
                    items: [payload],
                    category,
                    currency: currencyInfo?.transaction_currency || 'usd',
                    metadata: {
                        useAddressOnFile: true,
                        currencyInfo: currencyInfo,
                        name: `${user?.firstName} ${user?.lastName}`,
                        email: user?.email,
                        items: checkoutItems,
                        additionalFees,
                        ...railProcessorMeta,
                        ...additionalMetadata,
                    },
                },
                async paymentIntent => {
                    if (paymentIntent.status === 'succeeded') {
                        handleCompletePurchase(paymentIntent);
                    } else {
                        showToastMessage('Payment failed. Please try again.', 'error');
                    }
                },
            );
        } catch (error) {
            showToastMessage('Payment processing failed. Please try again.', 'error');
        } finally {
            setProcessing(false);
        }
    };

    const handlePaystackSubmit = async () => {
        setPaystackLoading(true);
        try {
            const currency = chargeCurrencyFromRail(paymentRail, currencyInfo?.transaction_currency || 'ngn');
            const provider = canUsePaystack ? 'paystack' : 'flutterwave';
            const res = (await dispatchPromise(
                createPaymentIntentForPurchase({
                    amount: Math.round(convertDollarsToCents(totalAmount)),
                    currency,
                    provider,
                    callback_url: window.location.href,
                    metadata: {
                        category,
                        items: checkoutItems.map(i => ({
                            id: i.id,
                            title: i.title,
                            price: i.price,
                            original_price: i.original_price ?? i.price,
                            quantity: i.quantity ?? 1,
                            currency: i.currency,
                            ...(i.details && i.details.length > 0 ? { details: i.details } : {}),
                        })),
                        metadata: { ...railProcessorMeta, ...additionalMetadata },
                    },
                }),
            )) as IPaystackPurchaseResponse;

            if (!res?.authorization_url) {
                showToastMessage(
                    provider === 'flutterwave'
                        ? 'Could not start Flutterwave checkout. Try again.'
                        : 'Could not start Paystack checkout. Try again.',
                    'error',
                );
                return;
            }
            try {
                sessionStorage.setItem(
                    paystackSessionKey,
                    JSON.stringify({
                        reference: res.reference,
                        saleId: res.saleId,
                        contestId: additionalMetadata?.contestId ?? null,
                        ...railProcessorMeta,
                        ...additionalMetadata,
                    }),
                );
            } catch {
                /* ignore */
            }
            window.location.href = res.authorization_url;
        } catch (err: any) {
            const msg =
                err?.data?.message ??
                err?.message ??
                (canUsePaystack ? 'Paystack checkout failed' : 'Flutterwave checkout failed');
            showToastMessage(msg, 'error');
            setPaystackLoading(false);
        }
    };

    const disableButton = () => {
        // Disable if no items or total is 0 or negative
        if (items.length === 0 || totalAmount <= 0) {
            return true;
        }

        if (paymentType === 'new_card') {
            return !isValidCard;
        }
        if (paymentType === 'saved_card') {
            return !selectedPaymentMethod;
        }
        return !paymentType;
    };

    // Stripe Elements options — must match PI: save on → off_session; save off → omit (not on_session)
    const options: StripeElementsOptions = useMemo(() => {
        const amountInCents = convertDollarsToCents(totalAmount);
        return {
            mode: 'payment',
            amount: Math.max(amountInCents, 1), // Ensure amount is at least 1 cent
            currency: checkoutCurrency,
            appearance: {
                theme: themeStyle === 'dark' ? 'night' : 'flat',
            },
            ...(saveForFuture ? { setup_future_usage: 'off_session' as const } : {}),
        };
    }, [totalAmount, checkoutCurrency, themeStyle, convertDollarsToCents, saveForFuture]);

    const renderPaymentSelector = useMemo(() => {
        // Only render payment selector when modal is open and has valid items
        if (!open || items.length === 0 || totalAmount <= 0) {
            return null;
        }

        return (
            <Elements stripe={stripePromise} options={options}>
                <PaymentMethodSelector
                    key={`payment-${Math.max(convertDollarsToCents(totalAmount), 1)}`}
                    ref={paymentRef}
                    saveForFuture={saveForFuture}
                    onSaveForFutureChange={setSaveForFuture}
                    amount={Math.max(convertDollarsToCents(totalAmount), 1)}
                    onPaymentMethodSelected={type => {
                        setPaymentType(type);
                    }}
                    onSelectSavedCard={card => {
                        setSelectedPaymentMethod(card);
                    }}
                    preselectCard
                    onChange={change => {
                        if (change.complete) {
                            setIsValidCard(true);
                        } else {
                            setIsValidCard(false);
                        }
                    }}
                    callBack={async paymentIntent => {
                        if (isPaymentIntentResult(paymentIntent) && paymentIntent.status === 'succeeded') {
                            await handleCompletePurchase(paymentIntent);
                        } else if (isPaymentIntentResult(paymentIntent)) {
                            showToastMessage('Payment failed. Please try again.', 'error');
                        }
                    }}
                    currency={checkoutCurrency}
                    details={{
                        email: user?.email || '',
                        name: user ? `${user.firstName} ${user.lastName}` : '',
                        saveCard: saveForFuture,
                        address: {
                            line1: '',
                            line2: '',
                            city: '',
                            state: '',
                            postal_code: '',
                            country: 'US',
                        },
                    }}
                    metadata={{
                        items: checkoutItems,
                        category,
                        currency: checkoutCurrency,
                        metadata: {
                            useAddressOnFile: true,
                            currencyInfo: currencyInfo,
                            name: `${user?.firstName} ${user?.lastName}`,
                            email: user?.email,
                            items: checkoutItems,
                            additionalFees,
                            ...railProcessorMeta,
                            ...additionalMetadata,
                        },
                    }}
                />
            </Elements>
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        open,
        totalAmount,
        checkoutItems,
        currencyInfo,
        checkoutCurrency,
        options,
        user,
        category,
        additionalFees,
        stripePromise,
        convertDollarsToCents,
        items.length,
    ]);

    return (
        <Dialog
            scroll="paper"
            open={open}
            onClose={() => onClose && onClose()}
            TransitionComponent={Transition}
            fullWidth={true}
            maxWidth={maxWidth}
            PaperProps={{
                style: {
                    backgroundColor: theme[themeStyle].modalBg,
                    width: device.isMobile ? '100vw' : '90vw',
                    maxWidth: device.isMobile ? '100%' : '600px',
                    borderRadius: device.isMobile ? 0 : 10,
                    padding: '10px',
                    margin: '0px',
                    marginTop: device.isMobile ? '0' : '30%',
                    border: `0.5px solid ${theme[themeStyle].darkGray60}`,
                    height: '100%',
                    maxHeight: device.isMobile ? '100%' : 'auto',
                },
            }}
            sx={{
                display: 'flex',
                height: { xs: '100vh', sm: '90%', lg: '90%' },
                alignItems: 'center',
                justifyContent: 'center',
                outline: 'none',
            }}>
            <Container
                sx={{
                    position: 'relative',
                    height: '100%',
                    alignItems: 'center',
                }}>
                <Box
                    sx={{
                        height: 'fit-content',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        justifyContent: 'center',
                        gap: device.isMobile ? 2 : 5,
                    }}>
                    <Box
                        sx={{
                            width: '100%',
                            height: device.isMobile ? '100%' : 'fit-content',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}>
                        <Typography
                            sx={{
                                textAlign: 'left',
                                fontSize: 24,
                                fontStyle: 'normal',
                                fontWeight: 600,
                                color: theme[themeStyle].primary_white,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                            }}>
                            {title}
                        </Typography>
                        <CloseIcon
                            onClick={onClose}
                            style={{ cursor: 'pointer' }}
                            stroke={theme[themeStyle].tag_white}
                        />
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            gap: 3,
                            width: '100%',
                        }}>
                        {/* Items List */}
                        {items.length > 0 ? (
                            <Paper
                                sx={{
                                    p: 2,
                                    mb: 2,
                                    backgroundColor: theme[themeStyle].modalBg,
                                    borderRadius: '8px',
                                    width: '100%',
                                    border: `1px solid ${theme[themeStyle].darkGray60}`,
                                }}>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        color: theme[themeStyle].primary_white,
                                        mb: 2,
                                    }}>
                                    Items
                                </Typography>
                                {items.map((item, index) => (
                                    <Box key={item.id || index} sx={{ mb: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                            {showItemImages && item.image && (
                                                <Box
                                                    sx={{
                                                        width: 60,
                                                        height: 60,
                                                        borderRadius: 1,
                                                        overflow: 'hidden',
                                                        flexShrink: 0,
                                                    }}>
                                                    <CustomLazyLoadImage
                                                        src={item.image}
                                                        alt={item.title}
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover',
                                                        }}
                                                        effect="blur"
                                                    />
                                                </Box>
                                            )}
                                            <Box sx={{ flex: 1 }}>
                                                <Typography
                                                    sx={{
                                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                                        color: theme[themeStyle].tag_white,
                                                        mb: 0.5,
                                                    }}>
                                                    {item.title}
                                                </Typography>
                                                {item.description && (
                                                    <Typography
                                                        sx={{
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            color: theme[themeStyle].tag_white,
                                                            fontSize: '0.875rem',
                                                            mb: 1,
                                                        }}>
                                                        {item.description}
                                                    </Typography>
                                                )}
                                                {showItemDetails && item.details && (
                                                    <Box sx={{ mb: 1 }}>
                                                        {item.details.map((detail, detailIndex) => (
                                                            <Box
                                                                key={detailIndex}
                                                                sx={{
                                                                    display: 'flex',
                                                                    justifyContent: 'space-between',
                                                                    mb: 0.5,
                                                                }}>
                                                                <Typography
                                                                    sx={{
                                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                        color: theme[themeStyle].tag_white,
                                                                        fontSize: '0.875rem',
                                                                    }}>
                                                                    {detail.label}
                                                                </Typography>
                                                                <Typography
                                                                    sx={{
                                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                        color: theme[themeStyle].tag_white,
                                                                        fontSize: '0.875rem',
                                                                    }}>
                                                                    {detail.value}
                                                                </Typography>
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                )}
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                    }}>
                                                    <Typography
                                                        sx={{
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            color: theme[themeStyle].gray_text,
                                                            fontSize: '0.875rem',
                                                        }}>
                                                        {item.quantity && item.quantity > 1
                                                            ? `Qty: ${item.quantity}`
                                                            : ''}
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                                            color: theme[themeStyle].primary_white,
                                                        }}>
                                                        {symbol}
                                                        {(
                                                            convertCentsToDollars(item.price) * (item.quantity || 1)
                                                        ).toFixed(2)}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                        {index < items.length - 1 && (
                                            <Divider sx={{ mt: 2, borderColor: theme[themeStyle].darkGray60 }} />
                                        )}
                                    </Box>
                                ))}
                            </Paper>
                        ) : (
                            <Paper
                                sx={{
                                    p: 3,
                                    mb: 2,
                                    backgroundColor: theme[themeStyle].modalBg,
                                    borderRadius: '8px',
                                    width: '100%',
                                    textAlign: 'center',
                                    border: `1px solid ${theme[themeStyle].darkGray60}`,
                                }}>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        color: theme[themeStyle].gray_text,
                                    }}>
                                    No items to purchase
                                </Typography>
                            </Paper>
                        )}

                        {/* Additional Fees */}
                        {additionalFees.length > 0 && (
                            <Paper
                                sx={{
                                    p: 2,
                                    mb: 2,
                                    backgroundColor: theme[themeStyle].modalBg,
                                    borderRadius: '8px',
                                    width: '100%',
                                    border: `1px solid ${theme[themeStyle].darkGray60}`,
                                }}>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        color: theme[themeStyle].primary_white,
                                        mb: 2,
                                    }}>
                                    Additional Fees
                                </Typography>
                                {additionalFees.map((fee, index) => (
                                    <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Box>
                                            <Typography
                                                sx={{
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    color: theme[themeStyle].primary_white,
                                                }}>
                                                {fee.label}
                                            </Typography>
                                            {fee.description && (
                                                <Typography
                                                    sx={{
                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                        color: theme[themeStyle].gray_text,
                                                        fontSize: '0.875rem',
                                                    }}>
                                                    {fee.description}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Typography
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                color: theme[themeStyle].primary_white,
                                            }}>
                                            {symbol}
                                            {convertCentsToDollars(fee.amount).toFixed(2)}
                                        </Typography>
                                    </Box>
                                ))}
                            </Paper>
                        )}

                        {/* Total */}
                        {items.length > 0 && totalAmount > 0 && (
                            <Paper
                                sx={{
                                    p: 2,
                                    mb: 2,
                                    backgroundColor: theme[themeStyle].modalBg,
                                    borderRadius: '8px',
                                    width: '100%',
                                    border: `1px solid ${theme[themeStyle].darkGray60}`,
                                }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            color: theme[themeStyle].primary_white,
                                        }}>
                                        Total
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            color: theme[themeStyle].primary_white,
                                        }}>
                                        {symbol}
                                        {totalAmount.toFixed(2)}
                                    </Typography>
                                </Box>
                            </Paper>
                        )}

                        {/* Payment Method */}
                        {items.length > 0 && totalAmount > 0 && canUseStripe && (
                            <Box sx={{ mb: 4, width: '100%' }}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        color: theme[themeStyle].primary_white,
                                        mb: 2,
                                    }}>
                                    Payment Method
                                </Typography>
                                {renderPaymentSelector}
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* Submit Button */}
                {items.length > 0 && totalAmount > 0 && (
                    <Box
                        sx={{
                            width: '100%',
                            height: 'fit-content',
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            bottom: device.isMobile ? (window.outerHeight - window.innerHeight) * 0.3 : 0,
                            left: 0,
                            padding: device.isMobile ? 3 : 0,
                        }}>
                        {canUseStripe && (
                            <Button
                                onClick={handleSubmit}
                                sx={{
                                    height: '52px',
                                    borderRadius: 100,
                                    fontSize: 16,
                                    fontWeight: 600,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginTop: '20px',
                                    marginBottom: '20px',
                                    textDecoration: 'none',
                                    backgroundColor: theme[themeStyle].primary_orange,
                                    color: theme[themeStyle].pure_black,
                                    textTransform: 'none',
                                    width: '100%',
                                    '&:hover': {
                                        border: `2px solid ${theme[themeStyle].primary_orange}`,
                                        backgroundColor: theme[themeStyle].pure_black,
                                        color: theme[themeStyle].primary_orange,
                                    },
                                    ':disabled': {
                                        backgroundColor: theme[themeStyle].darkGray60,
                                        color: theme[themeStyle].another_gray_color,
                                        border: 'none',
                                    },
                                }}
                                disabled={!!disableButton() || processing}>
                                {processing ? 'Processing...' : `Pay ${symbol}${totalAmount.toFixed(2)}`}
                            </Button>
                        )}
                        {(canUsePaystack || canUseFlutterwave) && (
                            <Button
                                onClick={handlePaystackSubmit}
                                sx={{
                                    height: '52px',
                                    borderRadius: 100,
                                    fontSize: 16,
                                    fontWeight: 600,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    textDecoration: 'none',
                                    backgroundColor: 'transparent',
                                    color: theme[themeStyle].primary_orange,
                                    textTransform: 'none',
                                    marginBottom: '20px',
                                    width: '100%',
                                    border: `2px solid ${theme[themeStyle].primary_orange}`,
                                    '&:hover': {
                                        backgroundColor: theme[themeStyle].primary_orange,
                                        color: theme[themeStyle].pure_black,
                                    },
                                    ':disabled': {
                                        backgroundColor: theme[themeStyle].darkGray60,
                                        color: theme[themeStyle].another_gray_color,
                                        border: 'none',
                                    },
                                }}
                                disabled={paystackLoading || processing}>
                                {paystackLoading
                                    ? canUsePaystack
                                        ? 'Opening Paystack…'
                                        : 'Opening Flutterwave…'
                                    : canUsePaystack
                                    ? 'Pay with Paystack'
                                    : 'Pay with Flutterwave'}
                            </Button>
                        )}
                        {!canUseStripe && !canUsePaystack && !canUseFlutterwave && (
                            <Typography
                                sx={{
                                    mb: 2,
                                    color: theme[themeStyle].tag_white,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: 12,
                                    textAlign: 'center',
                                }}>
                                Payment is currently unavailable for your region.
                            </Typography>
                        )}
                    </Box>
                )}
            </Container>
        </Dialog>
    );
};

export { GenericPaymentModal };
